<template>
  <v-container fluid class="chatbot-container pa-0">
    <v-layout row wrap justify-center>
      <v-flex xs12 sm10 md8 lg6>
        <!-- Header -->
        <v-toolbar dark color="primary" class="chat-header hidden-xs-only">
          <v-toolbar-title>
            <v-icon class="mr-2">chat</v-icon>
            WeServe Call Center Chat
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="clearChat" :disabled="loading">
            <v-icon>refresh</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card class="chat-card elevation-6">
          <!-- Messages Area -->
          <v-card-text class="messages-container" ref="messagesContainer">
            <div v-if="messages.length === 0" class="text-xs-center pa-4">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="message-wrapper"
            >
              <div :class="['message-bubble', message.type]">
                <div class="message-content">
                  <img
                    v-if="message.type === 'bot'"
                    :src="chatbotIcon"
                    alt="Bot"
                    class="message-icon bot-icon"
                  />
                  <v-icon v-else small class="message-icon"> person </v-icon>
                  <div class="message-text">{{ message.text }}</div>
                </div>
                <div class="message-time">{{ message.timestamp }}</div>
              </div>
            </div>
            <div v-if="loading" class="message-wrapper">
              <div class="message-bubble bot">
                <div class="message-content">
                  <img
                    :src="chatbotIcon"
                    alt="Bot"
                    class="message-icon bot-icon"
                  />
                  <div class="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>

          <!-- Input Area -->
          <v-divider></v-divider>
          <v-card-actions class="chat-input-area pa-2">
            <v-textarea
              v-model="userMessage"
              label="Type your message..."
              outlined
              hide-details
              rows="1"
              auto-grow
              @keydown.enter.exact.prevent="sendMessage"
              @keydown.enter.shift.exact="addNewLine"
              :disabled="loading"
              class="message-input"
              ref="messageInput"
            ></v-textarea>
            <v-btn
              color="#fddf00"
              icon
              flat
              large
              @click="sendMessage"
              :disabled="!userMessage.trim() || loading"
              class="ml-2 send-button"
            >
              <v-icon>send</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "Chatbot",
  data() {
    return {
      sessionId: "",
      messages: [],
      userMessage: "",
      loading: false,
      chatbotIcon: require("@/assets/back-office/icons/weserve_ai_chatbot_icon.png"),
      baseUrl:
        "https://n8n-workflow-internal.weserve.asia/webhook/weserve/call-center-chat",
    };
  },
  mounted() {
    this.loadSessionId();
    this.displayWelcomeMessage();
  },
  methods: {
    /**
     * Generate a unique session ID using timestamp and random number
     */
    generateSessionId() {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 15);
      return `session_${timestamp}_${random}`;
    },

    /**
     * Load session ID from localStorage or create a new one
     */
    loadSessionId() {
      let storedSessionId = localStorage.getItem("chatSessionId");

      if (!storedSessionId) {
        storedSessionId = this.generateSessionId();
        localStorage.setItem("chatSessionId", storedSessionId);
      }

      this.sessionId = storedSessionId;
    },

    /**
     * Display welcome message when chat starts
     */
    displayWelcomeMessage() {
      const welcomeMessage = {
        type: "bot",
        text: "สวัสดีค่ะ! ยินดีต้อนรับสู่ WeServe 👋 \n Wevee พร้อมช่วยเหลือคุณ 24/7 ค่ะ\n\n ฉันช่วยอะไรคุณได้บ้าง? \n- ตอบคำถามเกี่ยวกับบริการของเรา\n- ติดต่อทีมงาน \n- แจ้งคำขอรีฟันออเดอร์\n พิมพ์คำถามของคุณได้เลยค่ะ หรือเลือกหัวข้อที่สนใจด้านล่างนี้ 😊",
        timestamp: this.getCurrentTime(),
      };
      this.messages.push(welcomeMessage);
    },

    /**
     * Get current time formatted as HH:MM
     */
    getCurrentTime() {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    },

    /**
     * Send user message to N8N webhook and handle response
     */
    async sendMessage() {
      if (!this.userMessage.trim() || this.loading) {
        return;
      }

      const messageText = this.userMessage.trim();

      // Add user message to chat
      this.messages.push({
        type: "user",
        text: messageText,
        timestamp: this.getCurrentTime(),
      });

      // Clear input
      this.userMessage = "";

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // Set loading state
      this.loading = true;

      try {
        // Construct N8N webhook URL
        const url = `${this.baseUrl}`;

        // Prepare payload
        let payload = {
          user_id: this.$route.query.userId || 6,
          message: messageText,
          language: "th",
        };

        // Send request to N8N
        const response = await axios.post(url, payload, {
          timeout: 30000, // 30 seconds timeout
        });

        // Handle response
        let botResponse = "";
        if (response.data) {
          if (
            response.data.length > 0 &&
            typeof response.data[0].output === "string"
          ) {
            botResponse = response.data[0].output;
          } else if (response.data.message) {
            botResponse = response.data.message;
          } else if (response.data.response) {
            botResponse = response.data.response;
          } else {
            botResponse = JSON.stringify(response.data);
          }
        } else {
          botResponse =
            "อืม... เรื่องนี้อาจจะเกินความสามารถของฉันไปหน่อยนะคะ\n\nสิ่งที่ฉันช่วยได้ตอนนี้:\n✓ แนะนำบริการของ WeServe\n✓ ช่องทางการติดต่อทีมงาน\n✓ แจ้งคำขอรีฟันออเดอร์\n\nมีอะไรในหัวข้อเหล่านี้ให้ช่วยไหมคะ?";
        }

        // Add bot response to chat
        this.messages.push({
          type: "bot",
          text: botResponse,
          timestamp: this.getCurrentTime(),
        });
      } catch (error) {
        console.error("Error sending message to N8N:", error);

        // Add error message to chat
        let errorMessage = "Sorry, I encountered an error. Please try again.";

        if (error.code === "ECONNABORTED") {
          errorMessage =
            "Request timeout. Please check your connection and try again.";
        } else if (error.response) {
          errorMessage = `Server error: ${error.response.status}. Please try again later.`;
        } else if (error.request) {
          errorMessage = "Network error. Please check your connection.";
        }

        this.messages.push({
          type: "bot",
          text: errorMessage,
          timestamp: this.getCurrentTime(),
        });
      } finally {
        this.loading = false;

        // Scroll to bottom after response
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    /**
     * Scroll chat to bottom
     */
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    /**
     * Clear chat and start new session
     */
    clearChat() {
      this.messages = [];
      this.sessionId = this.generateSessionId();
      localStorage.setItem("chatSessionId", this.sessionId);
      this.displayWelcomeMessage();
    },

    /**
     * Add new line in textarea (Shift+Enter)
     */
    addNewLine() {
      this.userMessage += "\n";
    },
  },
};
</script>

<style lang="scss" scoped>
.chatbot-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  height: 100%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-card {
  height: 85vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  border-radius: 12px !important;
  overflow: hidden;
  margin: 20px;
}

.chat-header {
  flex-shrink: 0;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  background-color: #f5f5f5;
  padding: 16px !important;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;

    &:hover {
      background: #555;
    }
  }
}

.message-wrapper {
  margin-bottom: 16px;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-bubble {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &.user {
    background-color: #ffd600;
    margin-left: auto;
    border-bottom-right-radius: 4px;

    .message-content {
      justify-content: flex-end;

      .message-icon {
        order: 2;
        margin-left: 8px;
        margin-right: 0;
      }

      .message-text {
        text-align: right;
      }
    }

    .message-time {
      text-align: right;
    }
  }

  &.bot {
    background-color: #ffffff;
    margin-right: auto;
    border-bottom-left-radius: 4px;
  }
}

.message-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.message-icon {
  color: #666;
  margin-right: 8px;
  flex-shrink: 0;
}

.bot-icon {
  width: 50px;
  height: 50px;
}

.message-text {
  word-wrap: break-word;
  line-height: 1.5;
  color: #333;
  font-size: 14px;
  white-space: pre-line;
}

.message-time {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  text-align: left;
}

.chat-input-area {
  flex-shrink: 0;
  background-color: #ffffff;
  align-items: flex-end;
  padding-bottom: env(safe-area-inset-bottom) !important;
}

.message-input {
  flex: 1;
  max-height: 150px;
  font-size: 16px !important; // Prevents iOS zoom on focus

  ::v-deep textarea {
    font-size: 16px !important; // Prevents iOS zoom on focus
    line-height: 1.5 !important;
  }

  ::v-deep .v-input__control {
    min-height: 40px !important;
  }
}

.send-button {
  margin-bottom: 4px;
  flex-shrink: 0;
}

// Typing indicator animation
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #999;
    animation: typing 1.4s infinite;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

// Responsive adjustments
@media (max-width: 600px) {
  .chatbot-container {
    padding: 0;
    align-items: stretch;
  }

  .chat-card {
    height: 100vh;
    max-height: 100vh;
    border-radius: 0 !important;
    margin: 0;
    position: relative;
  }

  .chat-header {
    position: fixed !important;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    width: 100% !important;
    max-width: 100% !important;
  }

  .messages-container {
    padding: 12px !important;
    padding-top: 10px !important; // Toolbar height + padding
    padding-bottom: calc(90px + 12px) !important; // Input area height + padding
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .message-bubble {
    max-width: 85%;
  }

  .chat-input-area {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    padding: 8px !important;
    padding-bottom: calc(8px + env(safe-area-inset-bottom)) !important;
    border-top: 1px solid #e0e0e0;
  }
}

// Handle mobile keyboard visibility
@media (max-width: 600px) and (max-height: 500px) {
  .chat-card {
    height: 100vh;
  }

  .messages-container {
    flex: 1;
    min-height: 0;
  }
}
</style>